import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { enhancedApi } from '../services/api';
import { AssignmentModal } from '../components/AssignmentModal';
import { EnhancedEMRImport } from '../components/EnhancedEMRImport';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { 
  FileText, 
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  RefreshCw,
  Upload,
  Settings,
  Plus,
  Users,
  Activity,
  Heart,
  Stethoscope,
  Calendar,
  AlertTriangle,
  X,
  ChevronDown,
  CheckCircle,
  Save,
  Check,
  X as XIcon,
  Trash2
} from 'lucide-react';

interface Patient {
  id: number;
  patient_id: string;
  note_no: string;
  note_date: string;
  visit_id: string;
  trf_id: string;
  time_of_death: string;
  place_of_death: string;
  place_description: string;
  cause_of_death: string;
  created_by: string;
  created_time: string;
  cause_death_direct: string;
  cause_death_underlying1: string;
  cause_death_underlying2: string;
  cause_death_underlying3: string;
  interval_direct: string;
  interval_underlying1: string;
  interval_underlying2: string;
  interval_underlying3: string;
  interval_other: string;
  manner_of_death: string;
  injury_type: string;
  dept: string;
  unit_id: string;
  unit?: string; // Unit name from backend
  visit_time: string;
  trf_date: string;
  prov_diagnosis: string;
  final_diagnosis: string;
  audit_status: string;
  computed_audit_status: string;
  is_audit_required: boolean;
  import_source: string;
  age: string;
  gender: string;
}

export const MortalityList: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'cases' | 'enhanced_import'>('cases');
  
  // Redirect non-superusers away from enhanced_import tab
  useEffect(() => {
    if (activeTab === 'enhanced_import' && user?.role !== 'superuser') {
      setActiveTab('cases');
    }
  }, [activeTab, user?.role]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatients, setSelectedPatients] = useState<number[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fromMonth: null as number | null, // Show all months by default
    fromYear: null as number | null, // Show all years by default
    toMonth: null as number | null, // Show all months by default
    toYear: null as number | null, // Show all years by default
    department: '',
    unit: '',
    status: '',
    audit_required: '', // Add audit required filter
    import_source: '' // Add import source filter
  });
  const [departments, setDepartments] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  
  
  // Assignment modal states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCasesForAssignment, setSelectedCasesForAssignment] = useState<number[]>([]);
  
  // Manual case modal states
  const [showManualCaseModal, setShowManualCaseModal] = useState(false);
  const [manualCaseLoading, setManualCaseLoading] = useState(false);
  
  // View and Edit modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  
  // Inline editing states
  const [editingAuditRequired, setEditingAuditRequired] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<boolean | null>(null);
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [manualCaseForm, setManualCaseForm] = useState({
    patient_id: '',
    note_no: '',
    note_date: '',
    visit_id: '',
    trf_id: '',
    time_of_death: '',
    place_of_death: '',
    place_description: '',
    cause_of_death: '',
    cause_death_direct: '',
    cause_death_underlying1: '',
    cause_death_underlying2: '',
    cause_death_underlying3: '',
    interval_direct: '',
    interval_underlying1: '',
    interval_underlying2: '',
    interval_underlying3: '',
    interval_other: '',
    manner_of_death: '',
    injury_type: '',
    dept: '',
    unit_id: '',
    visit_time: '',
    trf_date: '',
    prov_diagnosis: '',
    final_diagnosis: '',
    age: '',
    age_unit: 'years',
    gender: ''
  });

  useEffect(() => {
    if (activeTab === 'cases') {
      fetchPatients();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDepartmentsAndUnits();
  }, []);

  useEffect(() => {
    if (activeTab === 'cases') {
      setCurrentPage(1); // Reset to first page when filters change
      fetchPatients();
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    if (activeTab === 'cases') {
      fetchPatients();
    }
  }, [currentPage, itemsPerPage]);

  const fetchDepartmentsAndUnits = async () => {
    try {
      const [departmentsResponse, unitsResponse] = await Promise.all([
        enhancedApi.departments.getDepartments(),
        enhancedApi.units.getUnits()
      ]);

      if (departmentsResponse.success) {
        setDepartments(departmentsResponse.data);
      }
      if (unitsResponse.success) {
        setUnits(unitsResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch departments and units:', error);
    }
  };


  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.patients.getPatients({ 
        case_type: 'mortality',
        page: currentPage,
        limit: itemsPerPage
      });
      if (response.success) {
        // response.patients should now be the patients array directly
        const patientsData = Array.isArray(response.patients) ? response.patients : [];
        
        // Apply filters
        const filteredPatients = applyFilters(patientsData);
        setPatients(filteredPatients);
        
        // Update pagination info
        console.log('API Response:', response);
        const responseWithPagination = response as any;
        console.log('Pagination data:', responseWithPagination.pagination);
        if (responseWithPagination.pagination) {
          setTotalPages(responseWithPagination.pagination.pages);
          setTotalItems(responseWithPagination.pagination.total);
          console.log('Set pagination - Pages:', responseWithPagination.pagination.pages, 'Total:', responseWithPagination.pagination.total);
        }
        console.log('Raw patients data from API:', patientsData);
        console.log('Applied filters:', filters);
        console.log('Filtered mortality cases:', filteredPatients);
        console.log('Total patients before filter:', patientsData.length);
        console.log('Total patients after filter:', filteredPatients.length);
      }
    } catch (error) {
      console.error('Failed to fetch mortality cases:', error);
      setPatients([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (patientsData: Patient[]) => {
    // Debug: Log filter state
    if (filters.fromMonth !== null || filters.fromYear !== null || filters.toMonth !== null || filters.toYear !== null) {
      console.log('Date filters active:', {
        fromMonth: filters.fromMonth,
        fromYear: filters.fromYear,
        toMonth: filters.toMonth,
        toYear: filters.toYear
      });
    }
    
    return patientsData.filter(patient => {
      // Date range filter - allow partial filters (from only, to only, or both)
      const dateString = patient.note_date ?? patient.created_time;
      if (!dateString) {
        // If no date available, skip date filtering for this record
        return true;
      }
      
      const patientDate = new Date(dateString);
      // Check if date is valid
      if (isNaN(patientDate.getTime())) {
        console.warn('Invalid date for patient:', patient.patient_id, 'date string:', dateString);
        // Invalid date, skip date filtering for this record
        return true;
      }
      
      // Smart auto-behavior: Auto-complete date range when only one side is set
      let fromDate: Date | null = null;
      let toDate: Date | null = null;
      
      // Determine "from" date
      if (filters.fromMonth !== null && filters.fromYear !== null) {
        fromDate = new Date(filters.fromYear, filters.fromMonth, 1);
        fromDate.setHours(0, 0, 0, 0);
      }
      
      // Determine "to" date
      if (filters.toMonth !== null && filters.toYear !== null) {
        toDate = new Date(filters.toYear, filters.toMonth + 1, 0); // Last day of toMonth
        toDate.setHours(23, 59, 59, 999);
      }
      
      // Smart auto-complete logic:
      // If only "From" is set → treat as "only that month" (auto-set To to end of that month)
      if (fromDate !== null && toDate === null) {
        toDate = new Date(filters.fromYear!, filters.fromMonth! + 1, 0); // Last day of fromMonth
        toDate.setHours(23, 59, 59, 999);
      }
      
      // If only "To" is set → treat as "up to end of that month" (auto-set From to start of that month)
      if (fromDate === null && toDate !== null) {
        fromDate = new Date(filters.toYear!, filters.toMonth!, 1); // First day of toMonth
        fromDate.setHours(0, 0, 0, 0);
      }
      
      // Apply date filters if we have valid date range
      if (fromDate !== null || toDate !== null) {
        const patientDateCopy = new Date(patientDate);
        
        // Check "from" date filter
        if (fromDate !== null) {
          const patientDateStart = new Date(patientDateCopy);
          patientDateStart.setHours(0, 0, 0, 0);
          if (patientDateStart < fromDate) {
          return false;
          }
        }
        
        // Check "to" date filter
        if (toDate !== null) {
          const patientDateEnd = new Date(patientDateCopy);
          patientDateEnd.setHours(23, 59, 59, 999);
          if (patientDateEnd > toDate) {
            return false;
          }
        }
      }

      // Department filter
      if (filters.department && patient.dept !== filters.department) {
        return false;
      }

      // Unit filter
      if (filters.unit && patient.unit_id !== filters.unit) {
        return false;
      }

      // Status filter - use computed status
      if (filters.status && patient.computed_audit_status !== filters.status) {
        return false;
      }

      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          patient.patient_id.toLowerCase().includes(searchLower) ||
          patient.note_no.toLowerCase().includes(searchLower) ||
          patient.cause_of_death.toLowerCase().includes(searchLower) ||
          patient.final_diagnosis.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => {
      const newFilters = {
      ...prev,
      [key]: value
      };
      
      // Debug: Log filter changes
      console.log('Filter change:', { key, value, previous: prev, newFilters });
      
      // Validate date range: ensure "from" <= "to" (only when both from and to are complete)
      if (newFilters.fromMonth !== null && newFilters.fromYear !== null &&
          newFilters.toMonth !== null && newFilters.toYear !== null) {
        const fromDate = new Date(newFilters.fromYear, newFilters.fromMonth, 1);
        const toDate = new Date(newFilters.toYear, newFilters.toMonth + 1, 0);
        
        if (fromDate > toDate) {
          // If invalid range, reset the "to" date when setting "from", or reset "from" when setting "to"
          if (key === 'fromMonth' || key === 'fromYear') {
            newFilters.toMonth = null;
            newFilters.toYear = null;
          } else if (key === 'toMonth' || key === 'toYear') {
            newFilters.fromMonth = null;
            newFilters.fromYear = null;
          }
        }
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      fromMonth: null,
      fromYear: null,
      toMonth: null,
      toYear: null,
      department: '',
      unit: '',
      status: '',
      audit_required: '',
      import_source: ''
    });
    setSearchTerm('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.department) count++;
    if (filters.unit) count++;
    if (filters.status) count++;
    if (filters.audit_required) count++;
    if (filters.import_source) count++;
    if (searchTerm) count++;
    return count;
  };

  const handlePatientSelection = (patientId: number) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleSelectAll = () => {
    if (!Array.isArray(patients)) return;
    
    if (selectedPatients.length === patients.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(patients.map(p => p.id));
    }
  };



  // Assignment functions
  const handleAssignAudit = () => {
    if (selectedPatients.length === 0) {
      addToast({
        type: 'warning',
        title: 'No Cases Selected',
        message: 'Please select at least one case to assign'
      });
      return;
    }
    setSelectedCasesForAssignment(selectedPatients);
    setShowAssignmentModal(true);
  };

  const handleAssignmentComplete = () => {
    setSelectedPatients([]);
    fetchPatients(); // Refresh the list
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Manual case functions
  const handleManualCaseFormChange = (field: string, value: string) => {
    setManualCaseForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetManualCaseForm = () => {
    setManualCaseForm({
      patient_id: '',
      note_no: '',
      note_date: '',
      visit_id: '',
      trf_id: '',
      time_of_death: '',
      place_of_death: '',
      place_description: '',
      cause_of_death: '',
      cause_death_direct: '',
      cause_death_underlying1: '',
      cause_death_underlying2: '',
      cause_death_underlying3: '',
      interval_direct: '',
      interval_underlying1: '',
      interval_underlying2: '',
      interval_underlying3: '',
      interval_other: '',
      manner_of_death: '',
      injury_type: '',
      dept: '',
      unit_id: '',
      visit_time: '',
      trf_date: '',
      prov_diagnosis: '',
      final_diagnosis: '',
      age: '',
      age_unit: 'years',
      gender: ''
    });
  };

  const handleCreateManualCase = async () => {
    // Validate required fields
    if (!manualCaseForm.patient_id || !manualCaseForm.time_of_death || !manualCaseForm.cause_of_death) {
      addToast({
        type: 'warning',
        title: 'Required Fields Missing',
        message: 'Please fill in required fields: Patient ID, Time of Death, and Cause of Death'
      });
      return;
    }

    try {
      setManualCaseLoading(true);
      
      // Prepare data for API call
      const caseData = {
        ...manualCaseForm,
        age: manualCaseForm.age,
        age_unit: manualCaseForm.age_unit,
        import_source: 'manual',
        audit_status: 'pending',
        is_audit_required: true
      };

      // Call API to create manual case
      const response = await enhancedApi.patients.createMortalityCase(caseData);
      
      if (response.success) {
        addToast({
          type: 'success',
          title: 'Case Created',
          message: 'Manual mortality case created successfully!'
        });
        setShowManualCaseModal(false);
        resetManualCaseForm();
        fetchPatients(); // Refresh the list
      } else {
        addToast({
          type: 'error',
          title: 'Creation Failed',
          message: `Failed to create case: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Failed to create manual case:', error);
      addToast({
        type: 'error',
        title: 'Creation Failed',
        message: 'Failed to create manual case. Please try again.'
      });
    } finally {
      setManualCaseLoading(false);
    }
  };

  const handleViewPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowViewModal(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowEditModal(true);
  };

  // Inline editing functions
  const handleStartEditAuditRequired = (patient: Patient) => {
    setEditingAuditRequired(patient.id);
    setEditingValue(patient.is_audit_required);
  };

  const handleSaveAuditRequired = async (patient: Patient) => {
    try {
      const response = await enhancedApi.admin.updatePatientAuditRequired(patient.id, editingValue!);
      if (response.success) {
        // Update the patient in the local state
        setPatients(prevPatients => 
          prevPatients.map(p => 
            p.id === patient.id 
              ? { 
                  ...p, 
                  is_audit_required: editingValue!
                  // Note: audit_status is now computed from assignment table, no need to update it here
                }
              : p
          )
        );
        addToast({
          type: 'success',
          title: 'Status Updated',
          message: 'Audit required status updated successfully'
        });
        setEditingAuditRequired(null);
        setEditingValue(null);
      } else {
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: `Failed to update audit required status: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Failed to update audit required status:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update audit required status. Please try again.'
      });
    }
  };

  const handleCancelEditAuditRequired = () => {
    setEditingAuditRequired(null);
    setEditingValue(null);
  };

  // Delete functions
  const handleDeletePatient = (patient: Patient) => {
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!patientToDelete) return;
    
    try {
      setDeleteLoading(true);
      const response = await enhancedApi.admin.deleteMortalityCase(patientToDelete.id);
      
      if (response.success) {
        // Remove the patient from the local state
        setPatients(prevPatients => 
          prevPatients.filter(p => p.id !== patientToDelete.id)
        );
        addToast({
          type: 'success',
          title: 'Case Deleted',
          message: 'Mortality case deleted successfully'
        });
        setShowDeleteModal(false);
        setPatientToDelete(null);
      } else {
        addToast({
          type: 'error',
          title: 'Delete Failed',
          message: `Failed to delete case: ${response.message}`
        });
      }
    } catch (error: any) {
      console.error('Failed to delete case:', error);
      
      // Extract specific error message from AxiosError if available
      let errorMessage = 'Failed to delete case. Please try again.';
      if (error?.response?.data?.message) {
        errorMessage = `Failed to delete case: ${error.response.data.message}`;
      } else if (error?.message) {
        errorMessage = `Failed to delete case: ${error.message}`;
      }
      
      addToast({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPatientToDelete(null);
  };

  const handleUpdatePatient = async (updatedPatient: Patient) => {
    try {
      setLoading(true);
      
      // Add case_type to the update data so backend knows which table to update
      const updateData = {
        ...updatedPatient,
        case_type: 'mortality'
      };
      
      // Call the backend API to update the mortality case
      const response = await enhancedApi.patients.updatePatient(updatedPatient.id, updateData);
      
      if (response.success) {
        // Refresh the list to get the updated data from the database
        await fetchPatients();
        setShowEditModal(false);
        setSelectedPatient(null);
        addToast({
          type: 'success',
          title: 'Case Updated',
          message: 'Mortality case updated successfully'
        });
      } else {
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: `Failed to update mortality case: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Error updating mortality case:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Error updating mortality case. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'badge badge-warning',
      assigned: 'badge badge-info',
      in_progress: 'badge badge-primary',
      submitted: 'badge badge-success',
      completed: 'badge badge-success',
      approved: 'badge badge-success',
      rejected: 'badge badge-error',
      not_required: 'badge badge-secondary',
      unassigned: 'badge badge-warning'
    };
    
    return (
      <span className={statusClasses[status as keyof typeof statusClasses] || 'badge badge-secondary'}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filteredPatients = Array.isArray(patients) ? patients.filter(patient => {
    // Search filter
    const matchesSearch = !searchTerm || (
      patient.patient_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.cause_of_death?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.final_diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Status filter - use computed status
    const matchesStatus = !filters.status || patient.computed_audit_status === filters.status;
    
    // Audit required filter
    const matchesAuditRequired = filters.audit_required === '' || 
      (filters.audit_required === '1' && patient.is_audit_required) ||
      (filters.audit_required === '0' && !patient.is_audit_required);
    
    // Department filter
    const matchesDepartment = !filters.department || patient.dept === filters.department;
    
    // Unit filter
    const matchesUnit = !filters.unit || patient.unit_id === filters.unit;
    
    // Import source filter
    const matchesImportSource = !filters.import_source || patient.import_source === filters.import_source;
    
    return matchesSearch && matchesStatus && matchesAuditRequired && matchesDepartment && matchesUnit && matchesImportSource;
  }) : [];

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Mortality List</h1>
              <p className="text-blue-100">Comprehensive mortality case management and EMR integration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="flex border-b">
              <button
                className={`px-6 py-4 font-medium text-sm ${
                  activeTab === 'cases'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('cases')}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Mortality Cases
              </button>
              {user?.role === 'superuser' && (
                <button
                  className={`px-6 py-4 font-medium text-sm ${
                    activeTab === 'enhanced_import'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('enhanced_import')}
                >
                  <Upload className="h-4 w-4 inline mr-2" />
                  Enhanced EMR Import
                </button>
              )}
        </div>
      </div>

      {/* Mortality Cases Tab */}
      {activeTab === 'cases' && (
        <div className="space-y-6">
          {/* Patient Actions */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search mortality cases..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button 
                  className={`btn btn-outline relative ${getActiveFiltersCount() > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>
              </div>
              <div className="flex gap-3">
                <button className="btn btn-outline">
                  <Download className="h-4 w-4" />
                  Export
                </button>
                {(user?.role === 'superuser' || user?.role === 'reviewer') && (
                  <button 
                    className="btn btn-primary"
                    onClick={handleAssignAudit}
                    disabled={selectedPatients.length === 0}
                  >
                    <Plus className="h-4 w-4" />
                    Assign Audit ({selectedPatients.length})
                  </button>
                )}
                {user?.role === 'superuser' && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowManualCaseModal(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Add Manual Mortality Case
                  </button>
                )}
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <X className="h-4 w-4" />
                    Clear All
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Date Range - From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Period</label>
                    <div className="flex gap-2">
                      <select
                        value={filters.fromMonth ?? ''}
                        onChange={(e) => {
                          const monthValue = e.target.value;
                          const parsedMonth = monthValue && monthValue !== '' ? parseInt(monthValue, 10) : null;
                          console.log('Month selected:', { monthValue, parsedMonth, currentFilters: filters });
                          handleFilterChange('fromMonth', parsedMonth);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Months</option>
                        <option value={0}>January</option>
                        <option value={1}>February</option>
                        <option value={2}>March</option>
                        <option value={3}>April</option>
                        <option value={4}>May</option>
                        <option value={5}>June</option>
                        <option value={6}>July</option>
                        <option value={7}>August</option>
                        <option value={8}>September</option>
                        <option value={9}>October</option>
                        <option value={10}>November</option>
                        <option value={11}>December</option>
                      </select>
                      <select
                        value={filters.fromYear ?? ''}
                        onChange={(e) => {
                          const yearValue = e.target.value;
                          const parsedYear = yearValue && yearValue !== '' ? parseInt(yearValue, 10) : null;
                          console.log('Year selected:', { yearValue, parsedYear });
                          handleFilterChange('fromYear', parsedYear);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Years</option>
                        {Array.from({ length: 6 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <option key={year} value={year}>{year}</option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Date Range - To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Period</label>
                    <div className="flex gap-2">
                      <select
                        value={filters.toMonth ?? ''}
                        onChange={(e) => handleFilterChange('toMonth', e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Months</option>
                        <option value={0}>January</option>
                        <option value={1}>February</option>
                        <option value={2}>March</option>
                        <option value={3}>April</option>
                        <option value={4}>May</option>
                        <option value={5}>June</option>
                        <option value={6}>July</option>
                        <option value={7}>August</option>
                        <option value={8}>September</option>
                        <option value={9}>October</option>
                        <option value={10}>November</option>
                        <option value={11}>December</option>
                      </select>
                      <select
                        value={filters.toYear ?? ''}
                        onChange={(e) => {
                          const yearValue = e.target.value;
                          const parsedYear = yearValue && yearValue !== '' ? parseInt(yearValue, 10) : null;
                          handleFilterChange('toYear', parsedYear);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Years</option>
                        {Array.from({ length: 6 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <option key={year} value={year}>{year}</option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Department Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                    <select
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Departments</option>
                      {departments.filter(d => d.is_active).map(dept => (
                        <option key={dept.id} value={dept.emr_code}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Unit Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                    <select
                      value={filters.unit}
                      onChange={(e) => handleFilterChange('unit', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Units</option>
                      {units.filter(u => u.is_active).map(unit => (
                        <option key={unit.id} value={unit.emr_code}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audit Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="assigned">Assigned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="not_required">Not Required</option>
                    </select>
                  </div>

                  {/* Audit Required Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audit Required</label>
                    <select
                      value={filters.audit_required}
                      onChange={(e) => handleFilterChange('audit_required', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Cases</option>
                      <option value="1">Required</option>
                      <option value="0">Not Required</option>
                    </select>
                  </div>

                  {/* Import Source Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Import Source</label>
                    <select
                      value={filters.import_source}
                      onChange={(e) => handleFilterChange('import_source', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Sources</option>
                      <option value="emr">EMR Import</option>
                      <option value="manual">Manual Entry</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Patient Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                <span className="ml-3 text-gray-600">Loading mortality cases...</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={Array.isArray(patients) && selectedPatients.length === patients.length && patients.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Patient ID</th>
                      <th>Time of Death</th>
                      <th>Cause of Death</th>
                      <th>Unit</th>
                      <th>Audit Status</th>
                      <th>Audit Required</th>
                      <th>Import Source</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.length > 0 ? filteredPatients.map((patient) => (
                      <tr key={patient.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPatients.includes(patient.id)}
                            onChange={() => handlePatientSelection(patient.id)}
                          />
                        </td>
                        <td>
                          <div className="font-medium">{patient.patient_id}</div>
                          <div className="text-sm text-gray-500">{patient.gender}, {patient.age}</div>
                        </td>
                        <td>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            {formatDate(patient.time_of_death)}
                          </div>
                          <div className="text-sm text-gray-500">{patient.place_description}</div>
                        </td>
                        <td>
                          <div className="max-w-xs truncate" title={patient.cause_of_death}>
                            {patient.cause_of_death}
                          </div>
                          <div className="text-sm text-gray-500">{patient.final_diagnosis}</div>
                        </td>
                        <td>
                          <div className="flex items-center">
                            <Activity className="h-4 w-4 text-gray-400 mr-2" />
                            {patient.unit || patient.unit_id}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(patient.computed_audit_status)}
                        </td>
                        <td>
                          {editingAuditRequired === patient.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={editingValue ? '1' : '0'}
                                onChange={(e) => setEditingValue(e.target.value === '1')}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="1">Yes</option>
                                <option value="0">No</option>
                              </select>
                              <button
                                onClick={() => handleSaveAuditRequired(patient)}
                                className="text-green-600 hover:text-green-800"
                                title="Save"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEditAuditRequired}
                                className="text-red-600 hover:text-red-800"
                                title="Cancel"
                              >
                                <XIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                patient.is_audit_required 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {patient.is_audit_required ? 'Yes' : 'No'}
                              </span>
                              {user?.role === 'superuser' && !['approved', 'completed', 'submitted'].includes(patient.computed_audit_status) && (
                                <button
                                  onClick={() => handleStartEditAuditRequired(patient)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Edit audit required status"
                                >
                                  <Edit className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            patient.import_source === 'emr' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {patient.import_source === 'emr' ? 'EMR Import' : 'Manual Entry'}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button 
                              className="btn btn-sm btn-outline"
                              onClick={() => handleViewPatient(patient)}
                              title="View case details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {user?.role === 'superuser' && (
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEditPatient(patient)}
                                title="Edit case"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {user?.role === 'superuser' && patient.import_source === 'manual' && (
                              <button 
                                className="btn btn-sm btn-error"
                                onClick={() => handleDeletePatient(patient)}
                                title="Delete case"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-gray-500">
                          {loading ? 'Loading mortality cases...' : 'No mortality cases found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {patients.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </div>
        </div>
      )}


      {/* Enhanced EMR Import Tab */}
      {activeTab === 'enhanced_import' && (
        <EnhancedEMRImport />
      )}

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        selectedCases={selectedCasesForAssignment}
        caseType="mortality"
        onAssignmentComplete={handleAssignmentComplete}
      />

      {/* Manual Case Modal */}
      {showManualCaseModal ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Add Manual Mortality Case</h2>
                <button
                  onClick={() => setShowManualCaseModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID *</label>
                    <input
                      type="text"
                      value={manualCaseForm.patient_id}
                      onChange={(e) => handleManualCaseFormChange('patient_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Enter patient ID"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={manualCaseForm.age}
                        onChange={(e) => handleManualCaseFormChange('age', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter age"
                      />
                      <select
                        value={manualCaseForm.age_unit || 'years'}
                        onChange={(e) => handleManualCaseFormChange('age_unit', e.target.value)}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                        <option value="months">months</option>
                        <option value="years">years</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={manualCaseForm.gender}
                      onChange={(e) => handleManualCaseFormChange('gender', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <select
                      value={manualCaseForm.dept}
                      onChange={(e) => handleManualCaseFormChange('dept', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select Department</option>
                      {departments.filter(d => d.is_active).map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select
                      value={manualCaseForm.unit_id}
                      onChange={(e) => handleManualCaseFormChange('unit_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select Unit</option>
                      {units.filter(u => u.is_active).map(unit => (
                        <option key={unit.id} value={unit.emr_code}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Death Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Death Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time of Death *</label>
                    <input
                      type="datetime-local"
                      value={manualCaseForm.time_of_death}
                      onChange={(e) => handleManualCaseFormChange('time_of_death', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Place of Death</label>
                    <input
                      type="text"
                      value={manualCaseForm.place_of_death}
                      onChange={(e) => handleManualCaseFormChange('place_of_death', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="e.g., Hospital, Home, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cause of Death *</label>
                    <textarea
                      value={manualCaseForm.cause_of_death}
                      onChange={(e) => handleManualCaseFormChange('cause_of_death', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      rows={3}
                      placeholder="Enter cause of death"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manner of Death</label>
                    <select
                      value={manualCaseForm.manner_of_death}
                      onChange={(e) => handleManualCaseFormChange('manner_of_death', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select Manner</option>
                      <option value="Natural">Natural</option>
                      <option value="Accident">Accident</option>
                      <option value="Suicide">Suicide</option>
                      <option value="Homicide">Homicide</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Additional Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admission Date</label>
                    <input
                      type="datetime-local"
                      value={manualCaseForm.visit_time}
                      onChange={(e) => handleManualCaseFormChange('visit_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provisional Diagnosis</label>
                    <input
                      type="text"
                      value={manualCaseForm.prov_diagnosis}
                      onChange={(e) => handleManualCaseFormChange('prov_diagnosis', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Enter provisional diagnosis"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Final Diagnosis</label>
                    <input
                      type="text"
                      value={manualCaseForm.final_diagnosis}
                      onChange={(e) => handleManualCaseFormChange('final_diagnosis', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Enter final diagnosis"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="mt-8 flex justify-end space-x-4">
                <button
                  onClick={() => setShowManualCaseModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateManualCase}
                  disabled={manualCaseLoading}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center"
                >
                  {manualCaseLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Case
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* View Patient Modal */}
      {showViewModal && selectedPatient ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Mortality Case Details</h3>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
                  <p className="text-gray-900">{selectedPatient.patient_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <p className="text-gray-900">{selectedPatient.age}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <p className="text-gray-900">{selectedPatient.gender}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admission Date</label>
                  <p className="text-gray-900">{formatDate(selectedPatient.visit_time)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time of Death</label>
                  <p className="text-gray-900">{formatDate(selectedPatient.time_of_death)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Place of Death</label>
                  <p className="text-gray-900">{selectedPatient.place_description}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <p className="text-gray-900">{selectedPatient.dept}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <p className="text-gray-900">{selectedPatient.unit || selectedPatient.unit_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Audit Status</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedPatient.computed_audit_status)}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cause of Death</label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPatient.cause_of_death}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Final Diagnosis</label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPatient.final_diagnosis}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowViewModal(false)}
                className="btn btn-outline"
              >
                Close
              </button>
              {user?.role === 'superuser' && (
                <button 
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditPatient(selectedPatient);
                  }}
                  className="btn btn-primary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Case
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Edit Patient Modal */}
      {showEditModal && selectedPatient ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Edit Mortality Case</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const updatedPatient: Patient = {
                ...selectedPatient,
                // Only update editable fields
                cause_of_death: formData.get('cause_of_death') as string,
                final_diagnosis: formData.get('final_diagnosis') as string
              };
              handleUpdatePatient(updatedPatient);
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="form-label">Patient ID</label>
                  <input type="text" name="patient_id" className="form-input bg-gray-100" defaultValue={selectedPatient.patient_id} disabled />
                </div>
                <div>
                  <label className="form-label">Age</label>
                  <input type="number" name="age" className="form-input bg-gray-100" defaultValue={selectedPatient.age} disabled />
                </div>
                <div>
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-select bg-gray-100" defaultValue={selectedPatient.gender} disabled>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Department</label>
                  <input type="text" name="dept" className="form-input bg-gray-100" defaultValue={selectedPatient.dept} disabled />
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <input type="text" name="unit_id" className="form-input bg-gray-100" defaultValue={selectedPatient.unit_id} disabled />
                </div>
                <div>
                  <label className="form-label">Admission Date</label>
                  <input 
                    type="datetime-local" 
                    name="visit_time" 
                    className="form-input bg-gray-100" 
                    defaultValue={selectedPatient.visit_time ? new Date(selectedPatient.visit_time).toISOString().slice(0, 16) : ''} 
                    disabled
                  />
                </div>
                <div>
                  <label className="form-label">Time of Death</label>
                  <input 
                    type="datetime-local" 
                    name="time_of_death" 
                    className="form-input bg-gray-100" 
                    defaultValue={selectedPatient.time_of_death ? new Date(selectedPatient.time_of_death).toISOString().slice(0, 16) : ''} 
                    disabled
                  />
                </div>
                <div>
                  <label className="form-label">Place of Death</label>
                  <input type="text" name="place_of_death" className="form-input bg-gray-100" defaultValue={selectedPatient.place_of_death} disabled />
                </div>
                <div>
                  <label className="form-label">Place Description</label>
                  <input type="text" name="place_description" className="form-input bg-gray-100" defaultValue={selectedPatient.place_description} disabled />
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Cause of Death</label>
                  <textarea 
                    name="cause_of_death" 
                    className="form-input" 
                    rows={3} 
                    defaultValue={selectedPatient.cause_of_death}
                    required 
                  ></textarea>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Final Diagnosis</label>
                  <textarea 
                    name="final_diagnosis" 
                    className="form-input" 
                    rows={3} 
                    defaultValue={selectedPatient.final_diagnosis}
                  ></textarea>
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  Update Case
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && patientToDelete && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Mortality Case</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this mortality case? This action cannot be undone.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Patient ID:</span>
                    <p className="text-gray-900">{patientToDelete.patient_id}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Note No:</span>
                    <p className="text-gray-900">{patientToDelete.note_no}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Cause of Death:</span>
                    <p className="text-gray-900 truncate">{patientToDelete.cause_of_death}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Import Source:</span>
                    <p className="text-gray-900">{patientToDelete.import_source === 'emr' ? 'EMR Import' : 'Manual Entry'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                disabled={deleteLoading}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="btn btn-error"
              >
                {deleteLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Case
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
